import type { components } from '@/types/api/v1'
import type { Edge, Node } from '@xyflow/react'

type AuditField = components['schemas']['AuditField']
type MappingKey = components['schemas']['MappingKey']

export type AuditStatus = 'error' | 'warning' | 'ok'
export type ValueRole = 'origin' | 'master' | 'dest'

export type GroupNodeData = {
  groupKey: string
  title: string
  technicalToken: string
  depth: number
  fieldCount: number
  errorCount: number
  warnCount: number
  showTechnical: boolean
  collapsed: boolean
  // y offsets (relative to the group top) for row separator lines
  separators: number[]
}

export type RowNodeData = {
  label: string
  technicalKey: string
  status: AuditStatus
  message: string
  showTechnical: boolean
}

export type ValueNodeData = {
  name: string
  sub: string
  role: ValueRole
  status: AuditStatus
  chosen: boolean
  showTechnical: boolean
  // reference-data deep link (see openReference in AuditFlow)
  linkObject?: string // fallback mapping-type object when no mapping_id is available
  linkSourceId?: string // origin/destination: filter reference data by source id
  linkMappingId?: string // origin/destination: fetch the mapping to read its true type
  linkGoid?: string // master: filter the entity grid by this goid
}

export type AuditEdgeData = {
  dashed: boolean
  tooltip: string | null
  color: string
}

export type AuditFlowModel = {
  nodes: Node[]
  edges: Edge[]
  totalHeight: number
}

// ----- geometry -----
export const DEFAULT_WIDTH = 950
export const META_X = 4
export const META_W = 264
const RIGHT_PAD = 16
const MIN_NODE_W = 170 // value boxes grow with the modal within these bounds;
const MAX_NODE_W = 330 // content truncates with an ellipsis past MAX_NODE_W
const COL_GAP = 72 // horizontal room between columns for the edge + arrow
const NODE_H = 50
const NODE_GAP = 10
const ROW_GAP = 16
const ROW_MIN_H = 56
const GROUP_HEADER_H = 40
const GROUP_PAD_TOP = GROUP_HEADER_H + 10
const GROUP_PAD_BOTTOM = 12
const GROUP_GAP = 12
const ROOT_GAP = 16
const INDENT_STEP = 8 // child group boxes inset this much per depth level

const GROUP_Z = 0
const EDGE_Z = 50
const NODE_Z = 100

// Three value columns (origin/master/dest) share the width left after the meta
// column. Boxes grow with the modal (clamped to MIN/MAX_NODE_W); the gap between
// them stays COL_GAP until boxes hit their max, after which the gap absorbs the
// extra width. Exported so the header row can align to the same columns.
export function columnGeometry(layoutWidth: number) {
  const colStart = META_X + META_W
  const inner = Math.max(
    3 * MIN_NODE_W + 2 * COL_GAP,
    layoutWidth - colStart - RIGHT_PAD,
  )
  const slot = inner / 3
  const nodeW = Math.round(
    Math.min(MAX_NODE_W, Math.max(MIN_NODE_W, slot - COL_GAP)),
  )
  return {
    originX: colStart,
    masterX: colStart + slot,
    destX: colStart + 2 * slot,
    nodeW,
  }
}

// ----- status + colors -----
export function computeStatus(field: AuditField): AuditStatus {
  if (field.error) return 'error'
  if (field.warning) return 'warning'
  return 'ok'
}

export function statusDotColor(status: AuditStatus): string {
  if (status === 'error') return 'var(--theme-error)'
  if (status === 'warning') return 'var(--theme-warning)'
  return 'var(--theme-success)'
}

function statusEdgeColor(status: AuditStatus): string {
  if (status === 'error') return 'var(--theme-error)'
  if (status === 'warning') return 'var(--theme-warning)'
  return 'hsl(var(--primary))'
}

// ----- path parsing + humanization -----
export function tokenize(path: string): string[] {
  const segs = path.split('.')
  const tokens: string[] = []
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i] ?? ''
    const next = segs[i + 1]
    if (next !== undefined && /^\d+$/.test(next)) {
      tokens.push(`${seg}.${next}`)
      i++
    } else {
      tokens.push(seg)
    }
  }
  return tokens
}

function baseOfToken(token: string): string {
  return token.replace(/\.\d+$/, '')
}

export function humanizeToken(token: string, enumerate: boolean): string {
  const match = token.match(/^(.+?)(?:\.(\d+))?$/)
  const base = match?.[1] ?? token
  const idx = match?.[2]
  const titled = base.charAt(0).toUpperCase() + base.slice(1)
  const singular = idx !== undefined ? titled.replace(/s$/, '') : titled
  return idx !== undefined && enumerate
    ? `${singular} ${Number.parseInt(idx, 10) + 1}`
    : singular
}

function toValueNode(key: MappingKey | null | undefined): {
  name: string
  sub: string
} {
  if (!key) return { name: '—', sub: '' }
  // Master/destination keys often carry only an id (goid / source_id) with no
  // human-readable name; fall back to the id so the node isn't a bare em-dash.
  const name = key.source_name ?? key.goid_name
  const id = key.source_id ?? key.goid ?? ''
  return { name: name ?? id ?? '—', sub: id }
}

function keyOf(k: MappingKey): string {
  return `${k.goid ?? ''}|${k.source_id ?? ''}|${k.mapping_id ?? ''}`
}

// Reference-data "object" values (ObjectContext / CROSSROADS_OBJECTS).
const LEAF_OBJECT: Record<string, string> = {
  site: 'site',
  product: 'product',
  products: 'product',
  terminal: 'terminal',
  terminals: 'terminal',
  supplier: 'counterparty',
  suppliers: 'counterparty',
  supply_owner: 'counterparty',
  company: 'counterparty',
}
const GOID_OBJECT: Record<string, string> = {
  product: 'product',
  site: 'site',
  terminal: 'terminal',
  company: 'counterparty',
  counterparty: 'counterparty',
}

function objectFromLeaf(leafToken: string): string | undefined {
  return LEAF_OBJECT[baseOfToken(leafToken)]
}

function objectFromGoid(goid: string | undefined): string | undefined {
  if (!goid) return undefined
  return GOID_OBJECT[goid.split(':')[0] ?? '']
}

// All options, plus any choice not present in the options list (defensive).
// Chosen options are sorted to the top so the selection reads by position.
function optionsWithChoice(
  options: MappingKey[],
  choice: MappingKey[],
): { key: MappingKey; chosen: boolean }[] {
  const chosenKeys = new Set(choice.map(keyOf))
  const optionKeys = new Set(options.map(keyOf))
  const extras = choice.filter((c) => !optionKeys.has(keyOf(c)))
  return [...options, ...extras]
    .map((key) => ({ key, chosen: chosenKeys.has(keyOf(key)) }))
    .sort((a, b) => Number(b.chosen) - Number(a.chosen))
}

// ----- normalized field view model -----
type ValueVM = {
  name: string
  sub: string
  chosen: boolean
  sourceId?: string
  goid?: string
  mappingId?: string
}
type FieldVM = {
  field: string
  leafToken: string
  label: string
  status: AuditStatus
  message: string
  objectType?: string
  origin: ValueVM
  masters: ValueVM[]
  destinations: ValueVM[]
  masterReason: string
  destReason: string
}

function toValueVM(
  key: MappingKey | null | undefined,
  chosen: boolean,
): ValueVM {
  return {
    ...toValueNode(key),
    chosen,
    sourceId: key?.source_id ?? undefined,
    goid: key?.goid ?? undefined,
    mappingId: key?.mapping_id ?? undefined,
  }
}

function normalizeField(d: AuditField): FieldVM {
  const status = computeStatus(d)
  const toVM = ({ key, chosen }: { key: MappingKey; chosen: boolean }) =>
    toValueVM(key, chosen)
  const tokens = tokenize(d.field)
  const leafToken = tokens[tokens.length - 1] ?? d.field
  return {
    field: d.field,
    leafToken,
    label: '',
    status,
    message: d.error || d.warning || '',
    objectType: objectFromLeaf(leafToken),
    origin: toValueVM(d.origin_value, true),
    masters: optionsWithChoice(
      d.master_options ?? [],
      d.master_choice ?? [],
    ).map(toVM),
    destinations: optionsWithChoice(
      d.destination_options ?? [],
      d.destination_choice ?? [],
    ).map(toVM),
    masterReason: d.master_reason,
    destReason: d.destination_reason,
  }
}

// ----- location tree -----
type TreeGroup = {
  key: string
  token: string
  label: string
  parentKey: string | null
  depth: number
  childKeys: string[]
  fields: FieldVM[]
  descFields: FieldVM[]
}

export type AuditTree = {
  groups: Map<string, TreeGroup>
  roots: string[]
}

export function buildTree(details: AuditField[]): AuditTree {
  const groups = new Map<string, TreeGroup>()
  const roots: string[] = []

  const ensure = (
    key: string,
    token: string,
    depth: number,
    parentKey: string | null,
  ) => {
    let g = groups.get(key)
    if (!g) {
      g = {
        key,
        token,
        label: '',
        parentKey,
        depth,
        childKeys: [],
        fields: [],
        descFields: [],
      }
      groups.set(key, g)
      if (parentKey) groups.get(parentKey)?.childKeys.push(key)
      else roots.push(key)
    }
    return g
  }

  for (const d of details) {
    const f = normalizeField(d)
    const tokens = tokenize(d.field)
    const groupTokens = tokens.length > 1 ? tokens.slice(0, -1) : tokens
    let parentKey: string | null = null
    let cum = ''
    groupTokens.forEach((tok, i) => {
      cum = cum ? `${cum}.${tok}` : tok
      ensure(cum, tok, i, parentKey)
      parentKey = cum
    })
    if (parentKey) groups.get(parentKey)?.fields.push(f)
  }

  // descendant fields (for counts)
  const collect = (key: string): FieldVM[] => {
    const g = groups.get(key)
    if (!g) return []
    let all = g.fields.slice()
    for (const ck of g.childKeys) all = all.concat(collect(ck))
    g.descFields = all
    return all
  }
  for (const r of roots) collect(r)

  // enumerate flags -> labels
  for (const g of groups.values()) {
    const siblings = [...groups.values()].filter(
      (x) =>
        x.parentKey === g.parentKey &&
        baseOfToken(x.token) === baseOfToken(g.token),
    )
    g.label = humanizeToken(g.token, siblings.length > 1)

    const baseCounts = new Map<string, number>()
    for (const f of g.fields) {
      const b = baseOfToken(f.leafToken)
      baseCounts.set(b, (baseCounts.get(b) ?? 0) + 1)
    }
    for (const f of g.fields) {
      const b = baseOfToken(f.leafToken)
      f.label = humanizeToken(f.leafToken, (baseCounts.get(b) ?? 0) > 1)
    }
  }

  return { groups, roots }
}

// ----- layout -----
function rowHeight(f: FieldVM): number {
  const maxCount = Math.max(1, f.masters.length, f.destinations.length)
  const stacked = maxCount * NODE_H + (maxCount - 1) * NODE_GAP
  return Math.max(stacked, ROW_MIN_H)
}

function stackYs(count: number, rowTop: number, height: number): number[] {
  const total = count * NODE_H + (count - 1) * NODE_GAP
  const start = rowTop + Math.max(0, (height - total) / 2)
  return Array.from(
    { length: count },
    (_, i) => start + i * (NODE_H + NODE_GAP),
  )
}

export function buildFlow(
  details: AuditField[],
  showTechnical: boolean,
  layoutWidth: number = DEFAULT_WIDTH,
  collapsed: ReadonlySet<string> = new Set(),
  // Which source columns get a reference-data link (only the currently selected
  // tenant's side). Master links are always available (tenant-agnostic).
  sourceLinks: { origin: boolean; destination: boolean } = {
    origin: true,
    destination: true,
  },
): AuditFlowModel {
  const tree = buildTree(details)
  const nodes: Node[] = []
  const edges: Edge[] = []
  const cols = columnGeometry(layoutWidth)

  // Value nodes are positioned relative to their (possibly indented) group, so
  // subtract the group's inset to keep the columns aligned in absolute space.
  const emitField = (
    f: FieldVM,
    parentKey: string,
    rowTop: number,
    absX: number,
  ) => {
    const h = rowHeight(f)
    const groupId = `group::${parentKey}`
    const colX = {
      origin: cols.originX - absX,
      master: cols.masterX - absX,
      dest: cols.destX - absX,
    }

    nodes.push({
      id: `row::${f.field}`,
      type: 'auditRow',
      parentId: groupId,
      position: { x: META_X, y: rowTop },
      data: {
        label: f.label,
        technicalKey: f.field,
        status: f.status,
        message: f.message,
        showTechnical,
      } satisfies RowNodeData,
      style: { width: META_W, height: h },
      draggable: false,
      selectable: false,
      zIndex: NODE_Z,
    })

    const originY = rowTop + (h - NODE_H) / 2
    nodes.push({
      id: `origin::${f.field}`,
      type: 'auditValue',
      parentId: groupId,
      position: { x: colX.origin, y: originY },
      data: {
        name: f.origin.name,
        sub: f.origin.sub,
        role: 'origin',
        status: 'ok',
        chosen: true,
        showTechnical,
        linkObject: sourceLinks.origin ? f.objectType : undefined,
        linkSourceId: sourceLinks.origin ? f.origin.sourceId : undefined,
        linkMappingId: sourceLinks.origin ? f.origin.mappingId : undefined,
      } satisfies ValueNodeData,
      width: cols.nodeW,
      height: NODE_H,
      draggable: false,
      selectable: false,
      zIndex: NODE_Z,
    })

    // Value nodes/edges stay neutral (purple; dashed = candidate). A field's
    // warning/error is surfaced by the row's status icon, not by coloring every
    // candidate arrow.
    const masterYs = stackYs(f.masters.length, rowTop, h)
    f.masters.forEach((m, i) => {
      const status: AuditStatus = 'ok'
      nodes.push({
        id: `master::${f.field}::${i}`,
        type: 'auditValue',
        parentId: groupId,
        position: { x: colX.master, y: masterYs[i] ?? rowTop },
        data: {
          name: m.name,
          sub: m.sub,
          role: 'master',
          status,
          chosen: m.chosen,
          showTechnical,
          linkObject: objectFromGoid(m.goid) ?? f.objectType,
          linkGoid: m.goid,
        } satisfies ValueNodeData,
        width: cols.nodeW,
        height: NODE_H,
        draggable: false,
        selectable: false,
        zIndex: NODE_Z,
      })
      edges.push({
        id: `e::${f.field}::om::${i}`,
        type: 'auditEdge',
        source: `origin::${f.field}`,
        target: `master::${f.field}::${i}`,
        data: {
          dashed: !m.chosen,
          tooltip: m.chosen ? f.masterReason || null : null,
          color: statusEdgeColor(status),
        } satisfies AuditEdgeData,
        zIndex: EDGE_Z,
      })
    })

    // Destination edges originate from the chosen master (or the first master,
    // or the origin when there are no masters).
    const chosenMasterIdx = f.masters.findIndex((m) => m.chosen)
    const destSourceIdx = chosenMasterIdx >= 0 ? chosenMasterIdx : 0
    const destSource = f.masters.length
      ? `master::${f.field}::${destSourceIdx}`
      : `origin::${f.field}`
    const destYs = stackYs(f.destinations.length, rowTop, h)
    f.destinations.forEach((d, i) => {
      const status: AuditStatus = 'ok'
      nodes.push({
        id: `dest::${f.field}::${i}`,
        type: 'auditValue',
        parentId: groupId,
        position: { x: colX.dest, y: destYs[i] ?? rowTop },
        data: {
          name: d.name,
          sub: d.sub,
          role: 'dest',
          status,
          chosen: d.chosen,
          showTechnical,
          linkObject: sourceLinks.destination ? f.objectType : undefined,
          linkSourceId: sourceLinks.destination ? d.sourceId : undefined,
          linkMappingId: sourceLinks.destination ? d.mappingId : undefined,
        } satisfies ValueNodeData,
        width: cols.nodeW,
        height: NODE_H,
        draggable: false,
        selectable: false,
        zIndex: NODE_Z,
      })
      edges.push({
        id: `e::${f.field}::md::${i}`,
        type: 'auditEdge',
        source: destSource,
        target: `dest::${f.field}::${i}`,
        data: {
          dashed: !d.chosen,
          tooltip: d.chosen ? f.destReason || null : null,
          color: statusEdgeColor(status),
        } satisfies AuditEdgeData,
        zIndex: EDGE_Z,
      })
    })
  }

  // pre-order: push the group node before its children, then recurse
  const layoutGroup = (key: string, yPos: number): number => {
    const g = tree.groups.get(key)
    if (!g) return 0
    const errorCount = g.descFields.filter((f) => f.status === 'error').length
    const warnCount = g.descFields.filter((f) => f.status === 'warning').length
    // Absolute inset of this box; the box position is relative to its parent so
    // a nested group only steps in by one INDENT_STEP from its parent.
    const absX = g.depth * INDENT_STEP
    const relX = g.parentKey ? INDENT_STEP : 0

    const isCollapsed = collapsed.has(key)
    const separators: number[] = []
    const groupNode: Node = {
      id: `group::${key}`,
      type: 'auditGroup',
      position: { x: relX, y: yPos },
      data: {
        groupKey: key,
        title: g.label,
        technicalToken: g.token,
        depth: g.depth,
        fieldCount: g.descFields.length,
        errorCount,
        warnCount,
        showTechnical,
        collapsed: isCollapsed,
        separators,
      } satisfies GroupNodeData,
      style: { width: layoutWidth - absX, height: 0 },
      draggable: false,
      selectable: false,
      zIndex: GROUP_Z + g.depth,
    }
    if (g.parentKey) groupNode.parentId = `group::${g.parentKey}`
    nodes.push(groupNode)

    if (isCollapsed) {
      groupNode.style = { width: layoutWidth - absX, height: GROUP_HEADER_H }
      return GROUP_HEADER_H
    }

    let cursor = GROUP_PAD_TOP
    g.fields.forEach((f, idx) => {
      emitField(f, key, cursor, absX)
      cursor += rowHeight(f)
      if (idx < g.fields.length - 1) separators.push(cursor + ROW_GAP / 2)
      cursor += ROW_GAP
    })
    for (const ck of g.childKeys) {
      const childH = layoutGroup(ck, cursor)
      cursor += childH + GROUP_GAP
    }
    const height = cursor + GROUP_PAD_BOTTOM
    groupNode.style = { width: layoutWidth - absX, height }
    return height
  }

  let rootCursor = 0
  for (const r of tree.roots) {
    const h = layoutGroup(r, rootCursor)
    rootCursor += h + ROOT_GAP
  }

  return { nodes, edges, totalHeight: rootCursor }
}
